import {
  Box,
  Button,
  Container,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Link as RouterLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useProducts } from '../../context/ProductsContext';

const ORANGE = '#FF6B00';
const DARK = '#1A1A1A';
const MUTED = '#555';
const SOFT = '#F8F9FA';

function Heading({ title, subtitle }) {
  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h3" sx={{ fontWeight: 800, color: DARK, fontSize: { xs: '1.75rem', md: '2.3rem' } }}>
        {title}
      </Typography>
      {subtitle ? (
        <Typography sx={{ mt: 1, color: MUTED, lineHeight: 1.7, fontSize: { xs: '1rem', md: '1.05rem' } }}>
          {subtitle}
        </Typography>
      ) : null}
    </Box>
  );
}

function parseRating(value) {
  const match = String(value).match(/^(\d)(?:\/5)?$/);
  if (!match) return null;
  const score = Number(match[1]);
  if (score < 1 || score > 5) return null;
  return score;
}

function CompareCellValue({ value, valueType }) {
  const rating = valueType === 'rating' ? parseRating(value) : null;

  if (rating) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ display: 'flex', gap: 0.4 }}>
          {[1, 2, 3, 4, 5].map((n) => (
            <Box
              key={n}
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: n <= rating ? ORANGE : '#E0E0E0',
              }}
            />
          ))}
        </Box>
        <Typography sx={{ color: ORANGE, fontWeight: 800, fontSize: '0.88rem' }}>
          {rating}/5
        </Typography>
      </Box>
    );
  }

  return (
    <Typography
      component="span"
      sx={{
        color: value === '★' ? ORANGE : value === '—' ? '#BDBDBD' : MUTED,
        fontWeight: value === '★' ? 900 : 500,
      }}
    >
      {value}
    </Typography>
  );
}

function CompareTable({ headers, rows, labelCol, footnote }) {
  return (
    <Box>
      <Typography sx={{ display: { xs: 'block', md: 'none' }, textAlign: 'center', mb: 1.5, color: MUTED, fontSize: '0.9rem' }}>
        Swipe to see full table
      </Typography>
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e4e4e4', borderRadius: 0, overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 760 }}>
          <TableHead>
            <TableRow sx={{ bgcolor: `${ORANGE}10` }}>
              <TableCell sx={{ fontWeight: 800, color: ORANGE, minWidth: 160, borderBottom: `2px solid ${ORANGE}` }}>{labelCol}</TableCell>
              {headers.map((h) => (
                <TableCell key={h} sx={{ fontWeight: 700, color: DARK, borderBottom: `2px solid ${ORANGE}`, whiteSpace: 'nowrap' }}>
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, idx) => (
              <TableRow key={row.feature || row.industry} sx={{ bgcolor: idx % 2 ? SOFT : '#fff' }}>
                <TableCell sx={{ fontWeight: 700, color: DARK }}>{row.feature || row.industry}</TableCell>
                {row.values.map((v, i) => (
                  <TableCell key={i}>
                    <CompareCellValue value={v} valueType={row.valueType} />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {footnote ? (
        <Typography sx={{ mt: 1.25, color: MUTED, fontStyle: 'italic', fontSize: '0.9rem' }}>{footnote}</Typography>
      ) : null}
    </Box>
  );
}

export default function ProductsSharedSections() {
  const { pageData } = useProducts();
  if (!pageData) return null;

  const {
    comparisonColumns,
    comparisonRows,
    comparisonFootnote,
    industryApplications,
    industryLegend,
    selectionGuide,
    deckingOptions,
    rackAccessories,
    accessoriesImage,
    accessoriesValueProps,
    settings,
  } = pageData;

  return (
    <>
      <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: SOFT, borderTop: '1px solid #ededed' }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <Heading title="System Comparison" subtitle="Compare all systems by density, access, rotation, cost and use-case." />
          <CompareTable labelCol="Feature" headers={comparisonColumns} rows={comparisonRows} footnote={comparisonFootnote} />
        </Container>
      </Box>

      <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: '#fff', borderTop: '1px solid #ededed' }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <Heading title="Typical Applications by Industry" />
          <CompareTable labelCol="Industry" headers={comparisonColumns} rows={industryApplications} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
            {industryLegend.map((item) => (
              <Typography key={item.symbol} sx={{ color: MUTED, fontSize: '0.95rem' }}>
                <Box component="span" sx={{ color: item.symbol === '★' ? ORANGE : DARK, fontWeight: 900, mr: 0.5 }}>
                  {item.symbol}
                </Box>
                {item.label}
              </Typography>
            ))}
          </Stack>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: SOFT, borderTop: '1px solid #ededed' }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <Heading title="Quick Selection Guide" subtitle="Shortlist the right system based on your priority." />
          <Box>
            {selectionGuide.map((item, idx) => (
              <Grid
                key={item.need}
                container
                spacing={2}
                alignItems="center"
                sx={{ py: 1.75, borderBottom: idx === selectionGuide.length - 1 ? 'none' : '1px solid #e5e5e5' }}
              >
                <Grid item xs={12} md={8}>
                  <Typography sx={{ color: MUTED, lineHeight: 1.6, fontSize: '0.98rem' }}>{item.need}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography sx={{ color: ORANGE, fontWeight: 800, fontSize: '1rem', textAlign: { xs: 'left', md: 'right' } }}>
                    {item.system}
                  </Typography>
                </Grid>
              </Grid>
            ))}
          </Box>
        </Container>
      </Box>

      <Box
        sx={{
          py: { xs: 6, md: 9 },
          bgcolor: '#fcfcfc',
          borderTop: '1px solid rgba(0,0,0,0.05)',
          position: 'relative',
          overflow: 'hidden',
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            width: { xs: 8, md: 12 },
            backgroundImage: 'repeating-linear-gradient(to bottom, #ff6a00 0 10px, transparent 10px 18px)',
            pointerEvents: 'none',
          },
        }}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 }, position: 'relative', zIndex: 1 }}>
          <Typography variant="overline" sx={{ color: ORANGE, fontWeight: 900, letterSpacing: 3, display: 'block', mb: 1 }}>
            Shelf surfaces
          </Typography>
          <Heading
            title="Decking Options"
            subtitle="Five surface choices — matched to load type, airflow, visibility, and fire-safety needs."
          />
          <Stack spacing={{ xs: 3, md: 3.5 }}>
            {deckingOptions.map((option, idx) => {
              const imageRight = idx % 2 === 0;
              return (
                <Box
                  key={option.id}
                  component={motion.div}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-40px' }}
                  transition={{ duration: 0.45, delay: 0.04 }}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: imageRight ? '1.15fr 0.85fr' : '0.85fr 1.15fr' },
                    gap: { xs: 2, md: 3.5 },
                    alignItems: 'center',
                    bgcolor: '#fff',
                    border: '1px solid #ececec',
                    borderLeft: { xs: `3px solid ${ORANGE}`, md: `4px solid ${ORANGE}` },
                    p: { xs: 2, md: 2.5 },
                  }}
                >
                  <Box sx={{ order: { xs: 2, md: imageRight ? 1 : 2 } }}>
                    <Typography sx={{ color: ORANGE, fontWeight: 900, fontSize: '0.78rem', letterSpacing: '0.18em', mb: 0.75 }}>
                      TYPE {String(idx + 1).padStart(2, '0')}
                    </Typography>
                    <Typography sx={{ fontWeight: 800, color: DARK, fontSize: { xs: '1.2rem', md: '1.45rem' }, lineHeight: 1.2, mb: 0.6 }}>
                      {option.name}
                    </Typography>
                    <Typography sx={{ color: MUTED, mb: 2, fontSize: '0.98rem', lineHeight: 1.55, maxWidth: 460 }}>
                      {option.tagline}
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.25 }}>
                      {option.benefits.map((benefit, bIdx) => (
                        <Box key={benefit} sx={{ display: 'flex', gap: 1.1, alignItems: 'flex-start', py: 0.75 }}>
                          <Typography sx={{ color: ORANGE, fontWeight: 800, fontSize: '0.78rem', minWidth: 18, mt: 0.15 }}>
                            {bIdx + 1}
                          </Typography>
                          <Typography sx={{ color: '#444', lineHeight: 1.45, fontSize: '0.92rem', fontWeight: 500 }}>
                            {benefit}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      order: { xs: 1, md: imageRight ? 2 : 1 },
                      bgcolor: SOFT,
                      border: '1px solid #e8e8e8',
                      height: { xs: 190, sm: 210, md: 230 },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: { xs: 1.25, md: 1.75 },
                    }}
                  >
                    <Box
                      component="img"
                      src={option.image}
                      alt={option.name}
                      sx={{ maxWidth: '100%', maxHeight: '100%', width: 'auto', height: 'auto', objectFit: 'contain', display: 'block' }}
                    />
                  </Box>
                </Box>
              );
            })}
          </Stack>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: '#fff', borderTop: '1px solid #ededed' }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <Heading title="Rack Accessories" subtitle="Safety and support components that protect inventory, people, and equipment." />
          <Box component="img" src={accessoriesImage} alt="Rack accessories overview" sx={{ width: '100%', display: 'block', border: '1px solid #e4e4e4', mb: 3 }} />
          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e4e4e4', borderRadius: 0 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: `${ORANGE}10` }}>
                  <TableCell sx={{ fontWeight: 800, color: ORANGE, borderBottom: `2px solid ${ORANGE}`, width: { xs: '42%', md: '35%' } }}>
                    Accessory
                  </TableCell>
                  <TableCell sx={{ fontWeight: 800, color: DARK, borderBottom: `2px solid ${ORANGE}` }}>
                    Purpose
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rackAccessories.map((acc, idx) => (
                  <TableRow key={acc.name} sx={{ bgcolor: idx % 2 ? '#fff' : '#fafafa' }}>
                    <TableCell sx={{ fontWeight: 700, color: DARK, fontSize: '0.92rem' }}>{acc.name}</TableCell>
                    <TableCell sx={{ color: MUTED, lineHeight: 1.6, fontSize: '0.9rem' }}>{acc.desc}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {accessoriesValueProps.map((vp) => (
              <Grid item xs={12} sm={6} md={3} key={vp.title}>
                <Paper elevation={0} sx={{ border: '1px solid #e4e4e4', borderTop: `3px solid ${ORANGE}`, borderRadius: 0, p: 2, height: '100%' }}>
                  <Typography sx={{ fontWeight: 800, color: DARK, mb: 0.5, fontSize: '0.95rem' }}>{vp.title}</Typography>
                  <Typography sx={{ color: MUTED, lineHeight: 1.55, fontSize: '0.88rem' }}>{vp.desc}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 6, md: 7 }, bgcolor: SOFT, borderTop: '1px solid #ededed' }}>
        <Container maxWidth="md" sx={{ px: { xs: 2, sm: 3 }, textAlign: 'center' }}>
          <Typography sx={{ fontWeight: 900, color: DARK, mb: 1, fontSize: { xs: '1.55rem', md: '1.95rem' } }}>
            {settings?.ctaTitle || 'Need help choosing the right system?'}
          </Typography>
          <Typography sx={{ color: MUTED, mb: 3, lineHeight: 1.7, maxWidth: 560, mx: 'auto', fontSize: { xs: '1rem', md: '1.06rem' } }}>
            {settings?.ctaSubtitle}
          </Typography>
          <Button
            component={RouterLink}
            to="/contact"
            variant="contained"
            size="large"
            endIcon={<ArrowForwardIcon />}
            sx={{ bgcolor: ORANGE, fontWeight: 800, borderRadius: 0, px: 4, '&:hover': { bgcolor: '#e66000' } }}
          >
            Request a Quote
          </Button>
        </Container>
      </Box>
    </>
  );
}
